/**
 * Jornadas de reparto: loading a truck, selling off it, and settling at the end of the day.
 *
 * Almost none of this is new machinery. A truck is a stock location, a driver's shift is a
 * caja, and selling off the truck is an ordinary sale. What this adds is the one thing that
 * was missing: making cash and goods settle together, as a single act that cannot be
 * half-finished.
 */

import {
  ZERO,
  ZERO_COST,
  ZERO_QTY,
  addMoney,
  centavos,
  costOf,
  esCamion,
  fechaEnNicaragua,
  milli,
  orderKey,
  subQty,
  weightedAverageCost,
  type AnyDomainEvent,
  type Centavos,
  type Milli,
} from '@abastece/shared';
import { credit, debit, entry, type JournalLine } from '../ledger/journal.js';
import { ajusteVendidoAntes, consumirLotesFefo, cuentaInventario, type Recibido } from '../inventario.js';
import { cerrarSesion } from './caja.js';
import {
  existenciaId,
  loteId,
  type ApplyResult,
  type Effect,
  type Jornada,
  type Reader,
} from '../state.js';

export async function reduceJornada(
  event: AnyDomainEvent,
  read: Reader,
): Promise<ApplyResult | null> {
  const rev = orderKey(event);

  switch (event.type) {
    case 'jornada.abierta':
      return abrir(event, read, rev);
    case 'jornada.liquidada':
      return liquidar(event, read, rev);
    default:
      return null;
  }
}

async function abrir(
  event: Extract<AnyDomainEvent, { type: 'jornada.abierta' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;

  if (await read.jornada(p.jornadaId)) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Esa jornada ya fue abierta' }] };
  }

  const abierta = await read.jornadaAbiertaDe(p.camion);
  if (abierta) {
    return {
      effects: [],
      rejections: [
        { eventId: event.id, reason: 'Ese camión ya tiene una jornada abierta. Liquídela primero.' },
      ],
    };
  }

  const sesion = await read.sesionCaja(p.sesionId);
  if (!sesion) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'La caja del repartidor no existe' }] };
  }
  if (sesion.estado === 'cerrada') {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Esa caja ya fue cerrada' }] };
  }
  if (sesion.ubicacion !== p.camion) {
    // Otherwise the cash would be reconciled against sales from somewhere else entirely.
    return {
      effects: [],
      rejections: [{ eventId: event.id, reason: 'La caja no corresponde a este camión' }],
    };
  }

  const jornada: Jornada = {
    id: p.jornadaId,
    camion: p.camion,
    repartidorId: p.repartidorId,
    sesionId: p.sesionId,
    fecha: p.fecha,
    estado: 'abierta',
    valorCargado: ZERO,
    valorDevuelto: ZERO,
    faltanteMercaderia: ZERO,
    faltanteEfectivo: ZERO,
    liquidadaEn: null,
    nota: null,
    rev,
  };

  return { effects: [{ kind: 'jornada.upsert', id: p.jornadaId, row: jornada }], rejections: [] };
}

async function liquidar(
  event: Extract<AnyDomainEvent, { type: 'jornada.liquidada' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;

  const jornada = await read.jornada(p.jornadaId);
  if (!jornada) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Esa jornada no existe' }] };
  }
  if (jornada.estado === 'liquidada') {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Esa jornada ya fue liquidada' }] };
  }

  // What comes back goes to a place the truck is not: written into the truck itself, the truck's
  // emptying overwrote it, and the goods left the stock while their cost stayed on the truck's
  // account (2026-10-20). The screen sends «tienda»; only another client could send a truck.
  if (esCamion(p.ubicacionRetorno)) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Lo que vuelve de la ruta va a la tienda o a la bodega, no a un camión' }] };
  }

  const sesion = await read.sesionCaja(jornada.sesionId);
  if (!sesion) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'No se encontró la caja del repartidor' }] };
  }
  if (sesion.estado === 'cerrada') {
    return {
      effects: [],
      rejections: [{ eventId: event.id, reason: 'La caja ya fue cerrada por separado. Liquide antes de cerrar.' }],
    };
  }

  const fecha = fechaEnNicaragua(event.clientTs);
  const cuentaCamion = cuentaInventario(jornada.camion);
  const cuentaRetorno = cuentaInventario(p.ubicacionRetorno);
  const effects: Effect[] = [];

  const enCamion = await read.existenciasDe(jornada.camion);
  const contado = new Map(p.conteoCamion.map((l) => [l.productoId, milli(l.cantidad)]));

  let faltante: Centavos = ZERO;
  let sobrante: Centavos = ZERO;
  let valorRetornado: Centavos = ZERO;
  const retornos: Recibido[] = [];

  for (const e of enCamion) {
    if (e.cantidad === 0) continue;

    /**
     * A product on the truck that nobody counted is counted as zero, not skipped. Skipping it
     * would let anything left off the sheet quietly stay "on the truck" forever — which is
     * exactly the hole a settlement exists to close.
     */
    const cantidadContada = contado.get(e.productoId) ?? ZERO_QTY;
    const diferencia = subQty(cantidadContada, e.cantidad);
    const valorDif = costOf(e.costoPromedio, milli(Math.abs(diferencia)));

    if (diferencia < 0) faltante = addMoney(faltante, valorDif);
    if (diferencia > 0) sobrante = addMoney(sobrante, valorDif);

    // Everything counted goes back to the warehouse; the truck ends empty either way.
    if (cantidadContada > 0) {
      const destinoId = existenciaId(e.productoId, p.ubicacionRetorno);
      const destino = await read.existencia(e.productoId, p.ubicacionRetorno);
      const cantidadDestino = destino?.cantidad ?? ZERO_QTY;
      const costoDestino = destino?.costoPromedio ?? ZERO_COST;
      const despues = {
        cantidad: (cantidadDestino + cantidadContada) as Milli,
        costoPromedio: weightedAverageCost(cantidadDestino, costoDestino, cantidadContada, e.costoPromedio),
      };
      retornos.push({
        antes: { cantidad: cantidadDestino, costoPromedio: costoDestino },
        despues,
        entrante: costOf(e.costoPromedio, cantidadContada),
        cuenta: cuentaInventario(p.ubicacionRetorno),
      });

      effects.push({
        kind: 'existencia.set',
        id: destinoId,
        row: {
          id: destinoId,
          productoId: e.productoId,
          ubicacion: p.ubicacionRetorno,
          cantidad: despues.cantidad,
          costoPromedio: despues.costoPromedio,
          ultimoMovimientoId: p.jornadaId,
          rev,
        },
      });

      valorRetornado = addMoney(valorRetornado, costOf(e.costoPromedio, cantidadContada));
    }

    /**
     * Batches come off the truck in full, whether or not the goods did.
     *
     * Everything the books said was on board is drawn down, earliest expiry first. The counted
     * part is recreated at the destination; the rest simply did not come back and is written
     * off with the quantity. Draining only the counted part would leave batches showing stock
     * on a truck that has already been settled, and the expiry board would keep pointing at it.
     */
    const lotesCamion = await read.lotesDe(e.productoId, jornada.camion);
    const salientes = consumirLotesFefo(lotesCamion, e.cantidad, rev);
    effects.push(...salientes);

    const lotesDestino = await read.lotesDe(e.productoId, p.ubicacionRetorno);
    let porDevolver = Math.min(cantidadContada, e.cantidad);
    for (const efecto of salientes) {
      if (porDevolver <= 0) break;
      const anterior = lotesCamion.find((x) => x.id === efecto.id)!;
      const salio = subQty(anterior.cantidadDisponible, efecto.row.cantidadDisponible);
      const movido = milli(Math.min(salio, porDevolver));
      if (movido <= 0) continue;
      porDevolver -= movido;

      const id = loteId(e.productoId, anterior.numero, p.ubicacionRetorno);
      const existente = lotesDestino.find((x) => x.id === id);
      effects.push({
        kind: 'lote.upsert',
        id,
        row: {
          id,
          productoId: e.productoId,
          numero: anterior.numero,
          vencimiento: anterior.vencimiento,
          cantidadRecibida: ((existente?.cantidadRecibida ?? 0) + movido) as Milli,
          cantidadDisponible: ((existente?.cantidadDisponible ?? 0) + movido) as Milli,
          compraId: anterior.compraId,
          ubicacion: p.ubicacionRetorno,
          rev,
        },
      });
    }

    // The truck is emptied regardless: what was counted went back, what was not is a shortfall.
    effects.push({
      kind: 'existencia.set',
      id: e.id,
      row: { ...e, cantidad: ZERO_QTY, ultimoMovimientoId: p.jornadaId, rev },
    });
  }

  const lineas: JournalLine[] = [];
  if (valorRetornado > 0) {
    lineas.push(
      debit(cuentaRetorno, valorRetornado, 'Devolución de ruta'),
      credit(cuentaCamion, valorRetornado),
    );
  }
  if (faltante > 0) {
    // Its own account, not general shrinkage: this shortfall has a name attached to it.
    lineas.push(debit('FALTANTE_REPARTO', faltante, 'No volvió del camión'), credit(cuentaCamion, faltante));
  }
  if (sobrante > 0) {
    lineas.push(debit(cuentaCamion, sobrante), credit('SOBRANTE_INVENTARIO', sobrante));
  }
  if (lineas.length > 0) {
    effects.push({
      kind: 'asiento',
      entry: entry(event.id, fecha, `Liquidación de reparto${p.nota ? `: ${p.nota}` : ''}`, lineas),
    });
  }
  const ajuste = ajusteVendidoAntes(event.id, fecha, retornos);
  if (ajuste) effects.push(ajuste);

  const cierre = cerrarSesion(sesion, {
    conteoCordobas: p.conteoEfectivo,
    conteoDolares: p.conteoDolares,
    // The driver hands over everything, float included: a truck keeps no cash between routes.
    queda: 0,
    tasaOficial: (await read.config()).fxTasaOficial,
    eventId: event.id,
    fecha: event.clientTs,
    concepto: 'Corte de caja del repartidor',
    rev,
    // Settling is the owner's (`liquidar_ruta`), counting the driver's cash in hand as they do.
    recibidoPor: event.actorId,
    cerradaPor: event.actorId,
  });
  effects.push(...cierre.effects);

  effects.push({
    kind: 'jornada.patch',
    id: p.jornadaId,
    rev,
    patch: {
      estado: 'liquidada',
      liquidadaEn: event.clientTs,
      valorDevuelto: addMoney(jornada.valorDevuelto, valorRetornado),
      faltanteMercaderia: faltante,
      faltanteEfectivo:
        cierre.diferenciaCordobas < 0 ? centavos(-cierre.diferenciaCordobas) : ZERO,
      nota: p.nota ?? null,
    },
  });

  return { effects, rejections: [] };
}
